import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import AuthModal from './AuthModal';
import MobileMenu from './MobileMenu';
import UserDashboard from './UserDashboard';
import { useAuth } from '../contexts/AuthContext';

const Logo: React.FC<{ scrolled: boolean }> = ({ scrolled }) => (
  <Link to="/" className="flex items-center space-x-3">
    <img src="https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/icons/OVL+-+NewLogo.webp" alt="Omniviewlabs Logo" className="h-10 sm:h-11 object-contain" />
    <div className="hidden sm:block">
      <span className="text-2xl font-bold tracking-tight font-heading" style={{ color: scrolled ? '#003B5C' : '#ffffff' }}>Omniviewlabs</span>
      <p className={`text-[9px] uppercase tracking-[0.2em] font-bold -mt-1 transition-colors duration-300 ${scrolled ? 'text-gray-500' : 'text-gray-300'}`}>Leave Nothing to Imagination</p>
    </div>
  </Link>
);

const NavLink: React.FC<{ href: string; children: React.ReactNode; scrolled: boolean; isHome: boolean }> = ({ href, children, scrolled, isHome }) => {
  const finalHref = isHome ? href : `/${href}`;

  return (
    <a
      href={finalHref}
      className={`font-semibold text-[11px] uppercase tracking-widest transition-all duration-300 whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-[#003B5C]' : 'text-gray-200 hover:text-white'}`}
    >
      {children}
    </a>
  );
};

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'login' | 'signup'>('login');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);

  const { currentUser, isAdmin, logout, userWallet } = useAuth();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, []);

  // ... rest of effect hooks ...
  useEffect(() => {
    document.body.classList.toggle('no-scroll', isModalOpen || isMenuOpen || isDashboardOpen);
  }, [isModalOpen, isMenuOpen, isDashboardOpen]);

  const openModal = (mode: 'login' | 'signup') => {
    setModalMode(mode);
    setIsModalOpen(true);
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'py-2.5 glass shadow-xl translate-y-3 mx-6 rounded-2xl' : 'py-8 bg-transparent'}`}>
        <div className="container-xl">
          <div className="flex justify-between items-center">
            <Logo scrolled={scrolled} />
            <nav className="flex items-center space-x-8">
              <div className="hidden lg:flex items-center space-x-6 xl:space-x-10">
                <NavLink href="#about" scrolled={scrolled} isHome={isHome}>About</NavLink>
                <NavLink href="#products" scrolled={scrolled} isHome={isHome}>Solutions</NavLink>
                <NavLink href="#other-features" scrolled={scrolled} isHome={isHome}>Features</NavLink>
                {isAdmin && <NavLink href="#admin-dashboard" scrolled={scrolled} isHome={isHome}><span className="text-[#FF5C00]">Admin DB</span></NavLink>}
                <NavLink href="#case-study" scrolled={scrolled} isHome={isHome}>Case Studies</NavLink>
                <NavLink href="#blog" scrolled={scrolled} isHome={isHome}>Insights</NavLink>
              </div>
              <div className="hidden lg:flex items-center space-x-3">
                {currentUser ? (
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setIsDashboardOpen(true)}
                      className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-full pl-4 pr-1 py-1 border border-white/10 transition-colors"
                      style={scrolled ? { backgroundColor: '#f1f5f9', borderColor: '#e2e8f0' } : {}}
                    >
                      <div className="flex flex-col items-end">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${scrolled ? 'text-[#003B5C]' : 'text-white'}`}>
                          {currentUser.displayName || currentUser.email?.split('@')[0]}
                        </span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {isAdmin && (
                            <span className="text-[7px] bg-[#FF5C00] text-white px-1.5 py-0.5 rounded font-black">
                              ADMIN
                            </span>
                          )}
                          {userWallet && (
                            <span className={`text-[8px] font-bold ${scrolled ? 'text-[#FF5C00]' : 'text-orange-300'}`}>
                              {userWallet.total_available} Credits
                            </span>
                          )}
                        </div>
                      </div>
                      {currentUser.photoURL ? (
                        <img
                          src={currentUser.photoURL}
                          alt="Profile"
                          className="h-8 w-8 rounded-full border-2 border-white shadow-sm"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-[#003B5C] text-white flex items-center justify-center text-[10px] font-bold border-2 border-white">
                          {(currentUser.displayName || currentUser.email || '?')[0].toUpperCase()}
                        </div>
                      )}
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => openModal('login')}
                      className={`font-black transition-colors duration-300 px-5 py-2 rounded-full text-[11px] uppercase tracking-widest ${scrolled ? 'text-gray-700 hover:text-[#003B5C]' : 'text-gray-300 hover:text-white'}`}
                    >
                      Login
                    </button>
                    <button
                      onClick={() => openModal('signup')}
                      className="bg-[#003B5C] hover:bg-[#002840] text-white font-black py-2.5 px-7 rounded-full transition-all duration-300 shadow-lg hover:shadow-blue-900/20 text-[11px] uppercase tracking-widest active:scale-95"
                    >
                      Join Us
                    </button>
                  </>
                )}
              </div>
              <div className="lg:hidden">
                <button
                  onClick={() => setIsMenuOpen(true)}
                  className={`p-2 rounded-xl transition-all ${scrolled ? 'text-[#003B5C] bg-blue-50 hover:bg-blue-100' : 'text-white bg-white/10 hover:bg-white/20'}`}
                  aria-label="Open menu"
                >
                  <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>
      <AuthModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} mode={modalMode} setMode={setModalMode} />
      <UserDashboard isOpen={isDashboardOpen} onClose={() => setIsDashboardOpen(false)} />
      <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} onOpenAuthModal={openModal} />
    </>
  );
};

export default Header;